import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type DragEvent } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../api';
import { can } from '../auth/me';
import { useMe } from '../auth/useMe';
import { CATALOG_KEY, CATEGORIES_KEY, useCategories, type Category } from './ItemForm';
import { useUnsavedGuard } from '../ui/unsaved';

/** Children ids by parent id (null: main categories), in the order the server gives (its sibling order). */
export type Groups = Map<string | null, string[]>;

export function groupsOf(categories: Category[]): Groups {
  const live = new Set(categories.map((c) => c.id));
  const out: Groups = new Map();
  for (const c of categories) {
    const parent = c.parentId !== null && live.has(c.parentId) ? c.parentId : null;
    out.set(parent, [...(out.get(parent) ?? []), c.id]);
  }
  return out;
}

/** The list with the item at `index` moved to `to` (clamped). */
export function moved<T>(list: readonly T[], index: number, to: number): T[] {
  const target = Math.max(0, Math.min(list.length - 1, to));
  const out = [...list];
  const [item] = out.splice(index, 1);
  out.splice(target, 0, item!);
  return out;
}

/** Every sibling group sorted as in `ids` (the default order, top-down). */
export function withOrder(groups: Groups, ids: readonly string[]): Groups {
  const place = new Map(ids.map((id, i) => [id, i]));
  const at = (id: string) => place.get(id) ?? Number.MAX_SAFE_INTEGER;
  return new Map([...groups].map(([parent, children]) => [parent, [...children].sort((a, b) => at(a) - at(b))]));
}

const same = (a: readonly string[] = [], b: readonly string[] = []) => a.length === b.length && a.every((x, i) => x === b[i]);

/** "Kolejność kategorii": ↑/↓ (or drag and drop) among siblings, "Zapisz", "Przywróć domyślną". */
export function CategoryOrderPage() {
  const { me } = useMe();
  const queryClient = useQueryClient();
  const list = useCategories(true);
  const categories = list.data?.categories ?? [];
  const saved = groupsOf(categories);
  const [draft, setDraft] = useState<Groups | null>(null);
  const [dragging, setDragging] = useState<{ parent: string | null; index: number } | null>(null);
  const [done, setDone] = useState(false);
  const groups = draft ?? saved;
  const changed = [...groups].filter(([parent, ids]) => !same(ids, saved.get(parent)));
  const canEdit = !!me && can(me, 'catalog.categories', 'edit');
  const byId = new Map(categories.map((c) => [c.id, c]));

  const defaults = useMutation({
    mutationFn: () => api<{ ids: string[] }>('/api/catalog/categories/default-order'),
    onSuccess: ({ ids }) => {
      setDone(false);
      setDraft(withOrder(groups, ids));
    },
  });
  const save = useMutation({
    mutationFn: async () => {
      for (const [parentId, ids] of changed) await api('/api/catalog/categories/order', { method: 'PUT', body: { parentId, ids } });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: CATEGORIES_KEY });
      await queryClient.invalidateQueries({ queryKey: CATALOG_KEY });
      setDraft(null);
      setDone(true);
    },
  });
  const error = [save.error, defaults.error].find((e) => e instanceof ApiError) as ApiError | undefined;
  // owner 2026-10-23 21:14: a new order not saved: leaving („← Wróć do katalogu”, another screen) asks first
  const guardRef = useUnsavedGuard({
    dirty: canEdit && changed.length > 0,
    save: async () => {
      await save.mutateAsync();
      return true;
    },
  });

  const move = (parent: string | null, index: number, to: number) => {
    setDone(false);
    setDraft(new Map(groups).set(parent, moved(groups.get(parent) ?? [], index, to)));
  };

  const renderGroup = (parent: string | null) => {
    const ids = groups.get(parent) ?? [];
    if (ids.length === 0) return null;
    const label = parent === null ? 'Kategorie główne' : `Podkategorie: ${byId.get(parent)?.name ?? ''}`;
    return (
      <ul className={parent === null ? 'list order-list' : 'order-list nested'} aria-label={label}>
        {ids.map((id, index) => {
          const name = byId.get(id)?.name ?? '';
          const drop = (e: DragEvent) => {
            e.preventDefault();
            if (dragging && dragging.parent === parent) move(parent, dragging.index, index);
            setDragging(null);
          };
          return (
            <li
              key={id}
              draggable={canEdit}
              onDragStart={() => setDragging({ parent, index })}
              onDragOver={(e) => dragging?.parent === parent && e.preventDefault()}
              onDrop={drop}
              onDragEnd={() => setDragging(null)}
            >
              <div className="order-row">
                <span>{name}</span>
                {canEdit && (
                  <span className="actions">
                    <button className="btn small" type="button" aria-label={`W górę: ${name}`} disabled={index === 0} onClick={() => move(parent, index, index - 1)}>
                      ↑
                    </button>
                    <button className="btn small" type="button" aria-label={`W dół: ${name}`} disabled={index === ids.length - 1} onClick={() => move(parent, index, index + 1)}>
                      ↓
                    </button>
                  </span>
                )}
              </div>
              {renderGroup(id)}
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <div ref={guardRef} className="page" data-feature-id="catalog.category_order">
      <p className="crumb">
        <Link to="/katalog">← Wróć do katalogu</Link>
      </p>
      <div className="page-head">
        <h1>Kolejność kategorii</h1>
      </div>
      <p className="muted">
        W tej kolejności są grupy w sprzętówce (na ekranie i w PDF) oraz listy kategorii do wyboru. Podkategorie przesuwasz w obrębie ich kategorii. Nowa
        kategoria trafia na koniec swojego poziomu, jeśli jego kolejność była już zapisana; inaczej obowiązuje kolejność domyślna (najpierw nagłośnienie).
      </p>
      {list.isLoading && (
        <p className="muted" role="status">
          Wczytywanie…
        </p>
      )}
      {list.data && categories.length === 0 && <p className="muted">Nie ma jeszcze kategorii.</p>}
      {renderGroup(null)}
      {error && (
        <p className="alert" role="alert">
          {error.message}
        </p>
      )}
      {done && (
        <p className="muted" role="status">
          Zapisano kolejność.
        </p>
      )}
      {canEdit && categories.length > 0 && (
        <div className="actions">
          <button className="btn primary" type="button" disabled={save.isPending || changed.length === 0} onClick={() => save.mutate()}>
            {save.isPending ? 'Zapisywanie…' : 'Zapisz'}
          </button>
          <button className="btn" type="button" disabled={defaults.isPending} onClick={() => defaults.mutate()}>
            Przywróć domyślną
          </button>
          {changed.length > 0 && (
            <button className="btn" type="button" onClick={() => setDraft(null)}>
              Anuluj zmiany
            </button>
          )}
        </div>
      )}
    </div>
  );
}
